import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import {
  IconCalendarOutline,
  IconClock2,
  IconExchange,
  IconStarOutline,
} from '@iwoca/icons';
import { Card } from '@iwoca/orion';
import Rive from '@rive-app/react-canvas';
import { clsx } from 'clsx';

import styles from './CheckEligibilityUK.module.css';
import { useIsInStep0EligibilityTest } from '../../../AnonymousSplitTest/useIsInStep0EligibilityTest';
import {
  useGetApplicationQuery,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../api/lendingApi/lendingApiQueries';
// @ts-expect-error does not recognise riv import.
import successTick from '../../../assets/animation/successTick.riv';
import { calculateEligibleAmount } from '../../../helpers/calculateEligibleAmount';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useNumberFormat } from '../../../hooks/useNumberFormat';
import { useSignupParams } from '../../../hooks/useSignupParams';
import { useSignupRouterUK } from '../../../hooks/useSignupRouter/useSignupRouterUK';
import { useSplitTestContext } from '../../../hooks/useSplitTestContext';
import { TGetStateResponse, TSignupStepsUK } from '../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { normaliseStepData } from '../../helpers/stateNormalisation';
import { CHECK_ELIGIBILITY_STEP_ID } from '../../helpers/steps';
import AccountCreatedModal from '../../SignupRoutes/AccountCreatedModal/AccountCreatedModal';

const CheckEligibilityUK = () => {
  const [navigatingToStep, setIsNavigatingToStep] =
    useState<TSignupStepsUK | null>(null);

  const { formatCurrency } = useNumberFormat();
  const { navigateSignup } = useSignupRouterUK();
  const { isDirectCustomer } = useSignupParams();

  const isInStep0EligibilityTest = useIsInStep0EligibilityTest();
  const stateKey = useContextStateKey();

  const { hasInterestOnlyEnabled } = useSplitTestContext();

  const { isStateFetching } = useGetStateQuery<TGetStateResponse>();
  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[CHECK_ELIGIBILITY_STEP_ID],
  );

  const { application } = useGetApplicationQuery();

  const requestedAmount = application?.requested_products?.[0].amount;

  const eligibleAmount = calculateEligibleAmount({
    annualTurnover: normalisedState?.last12MonthsTurnover || 0,
    requestedAmount,
  });

  // The variant collects the amount on the next step, so there is nothing to quote yet.
  const showGenericTitle =
    isInStep0EligibilityTest ||
    Boolean(requestedAmount && requestedAmount > eligibleAmount);

  const benefitsSize = hasInterestOnlyEnabled ? 'large' : 'medium';

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Check Eligibility',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  const navigateWithLoading = async (route: TSignupStepsUK) => {
    setIsNavigatingToStep(route);

    await navigateSignup({ route });

    setIsNavigatingToStep(null);
  };

  return (
    <>
      <section className={styles.checkEligibilityContainer}>
        <div className={styles.animationContainer}>
          <Rive
            src={successTick}
            className={styles.accountSuccessTickRive}
            data-testid="success-tick-animation"
          />
        </div>
        <h1 className={styles.title}>
          {showGenericTitle
            ? 'Good news, you’re eligible!*'
            : `You’re eligible for up to ${formatCurrency(eligibleAmount)}*`}
        </h1>
        <Card
          className={clsx(styles.benefitsCard, {
            [styles.largeBenefitsContainer]: benefitsSize === 'large',
          })}
        >
          <section className={styles.benefitsContainer}>
            <div className={styles.benefit}>
              <IconStarOutline className={styles.benefitIcon} />
              <p>Repay early for free at any time</p>
            </div>
            <div className={styles.benefit}>
              <IconCalendarOutline className={styles.benefitIcon} />
              <p>Borrow from 1 day to 60 months</p>
            </div>
            <div className={styles.benefit}>
              <IconClock2 className={styles.benefitIcon} />
              <p>Decision within 24 hours</p>
            </div>
            {hasInterestOnlyEnabled && (
              <div className={styles.benefit}>
                <IconExchange className={styles.benefitIcon} />
                <p>3-month interest-only repayment option</p>
              </div>
            )}
          </section>
          <p className={styles.terms}>
            <small>
              *Final terms are subject to the strength of your application
            </small>
          </p>
          <p className={styles.ctaLabel}>
            <strong>Applying won’t affect your credit score</strong>
          </p>
          <Button
            className={styles.nextButton}
            disabled={navigatingToStep === 'business' || isStateFetching}
            loading={navigatingToStep === 'applicant'}
            type="submit"
            onClick={() => navigateWithLoading('applicant')}
          >
            {isInStep0EligibilityTest ? 'Next' : 'Next, add personal details'}
          </Button>
        </Card>
        {!isDirectCustomer && (
          <Button
            variant="tertiary"
            className={styles.backButton}
            disabled={navigatingToStep === 'applicant' || isStateFetching}
            loading={navigatingToStep === 'business'}
            onClick={() => navigateWithLoading('business')}
            type="button"
          >
            Back
          </Button>
        )}
      </section>
      {isInStep0EligibilityTest && stateKey && (
        <AccountCreatedModal stateKey={stateKey} />
      )}
    </>
  );
};

export default CheckEligibilityUK;
